import { Button, IconButton, Tooltip, Typography, Box, Paper } from 'components/mui';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/mui';
import { CloseOutlinedIcon, PortraitIcon } from 'components/icons';
import useImgUploader from './useImgUploader';
import ButtonBase from '@mui/material/ButtonBase';
import { func, object } from 'prop-types';
import { Contact } from '../../constants/contact';

/**
 * @component ContactForm
 * @param {Object} props
 * @param {ContactInfo} props.contact
 * @param {function} props.dispatch
 * @returns {JSX.Element}
 */
function ImgUploader({ contact, dispatch }) {
    const state = useImgUploader(dispatch);

    return (
        <>
            {contact[Contact.Image]?.preview ? (
                <Paper
                    variant="outlined"
                    sx={{
                        position: 'relative',
                        borderRadius: 2,
                        p: 1,
                        width: '100%',
                        maxWidth: 160,
                        mx: 'auto',
                        mb: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Tooltip title="Reset">
                        <IconButton
                            size="small"
                            onClick={state.onDelete}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'grey.100' },
                            }}
                            aria-label="Remove image"
                        >
                            <CloseOutlinedIcon color="primary" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Box
                        component="img"
                        src={contact[Contact.Image]?.preview}
                        alt="user_image"
                        sx={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: '50%',
                            boxShadow: 1,
                        }}
                    />
                </Paper>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        minHeight: 136,
                        mb: 2,
                    }}
                >
                    <ButtonBase
                        onClick={state.onOpen}
                        sx={{
                            borderRadius: '50%',
                            width: 120,
                            height: 120,
                            border: theme => `1px dashed ${theme.palette.divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.default',
                            '&:hover': {
                                bgcolor: 'grey.100',
                            },
                        }}
                        aria-label="Upload image"
                    >
                        <PortraitIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                    </ButtonBase>
                </Box>
            )}
            <Dialog
                onClose={state.onClose}
                open={state.isOpenDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Typography variant="h6" color="primary">
                        Upload Profile Picture
                    </Typography>
                    <Tooltip title="Close">
                        <IconButton color="primary" onClick={state.onClose}>
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {!state.previewImg.file ? (
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                                height: 160,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderStyle: 'dashed',
                                bgcolor: 'background.default',
                                cursor: 'pointer',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Upload Image
                            </Typography>
                            <input type="file" hidden onChange={state.onChange} />
                        </Button>
                    ) : (
                        <Box
                            component="img"
                            src={state.previewImg.file}
                            alt="preview-img"
                            sx={{
                                maxWidth: 160,
                                maxHeight: 160,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                boxShadow: 1,
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
                    <Button
                        name={Contact.Image}
                        variant="contained"
                        fullWidth
                        onClick={state.onChoose}
                        disabled={!state.previewImg.file}
                    >
                        Upload
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={state.onReset}
                    >
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

ImgUploader.propTypes = {
    contact: object,
    dispatch: func,
};

export default ImgUploader;
